import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, FocusEventHandler, KeyboardEventHandler, PointerEvent, ReactNode } from 'react'

/**
 * TRAILING AFFORDANCES — the buttons that live at a field's trailing edge, inside its box:
 * CLEAR (an X, on every text field), REVEAL (an eye, on password fields) and the STEPPER (a
 * chevron pair on number fields, replacing the UA's own spin buttons, which `textField.css`
 * hides). One mechanism, because they share a rail and a focus rule.
 *
 * ★ PORTED FROM MONZA (`packages/ui/src/ui/clearable.tsx`), which solved this once already —
 * David, 2026-08-04: *"monza had to implement a custom numeric spinner to avoid the clash — take
 * a look"*. Donor rule: copied and rewritten, never depended on. Two deliberate differences:
 * Monza's version is Tailwind + lucide-react, this one is inline styles + inline SVG (⛔ Tailwind
 * does not scan `packages/core`, so a utility class written here is never generated), and Monza
 * gates the WHOLE rail on focus where here only the CLEAR is (see `shownOnFocus` below).
 *
 * ⚠ CLEAR STAYS RIGHT-MOST. Reveal and the stepper insert BEFORE it. The affordance that
 * destroys the value is the one whose position must never move under a finger that has learned
 * where it is.
 *
 * ⛔⛔ THE ONE REQUIREMENT EVERYTHING ELSE BENDS AROUND: THE FIELD MUST NEVER LOSE FOCUS. On a
 * phone, losing focus dismisses the virtual keyboard, and a control that closes the keyboard
 * mid-edit is worse than no control. Focus moves on **mousedown**, not on click — so these
 * buttons `preventDefault()` there and act on `click`. Focus is never handed over, so the
 * keyboard is never asked to go away.
 *
 * ⛔ `mousedown`, deliberately NOT `pointerdown`. Cancelling a pointer event has broader
 * suppression semantics — on some engines it also kills the compatibility `click`, giving a
 * button that holds focus perfectly and never does anything. `mousedown` cancellation is the
 * narrow, well-worn behaviour: focus suppressed, `click` still fires. On touch it is the
 * compatibility `mousedown` that moves focus, so the one guard covers both.
 *
 * ⛔ And NOT "let focus go, then put it back". `field.focus()` will not re-open the keyboard on
 * iOS outside a user gesture, and even inside one there is a visible flicker.
 *
 * 🔎 What tests can and cannot prove: `~/qa/appyoreflixuk-e2e/verify-field-affordances.mjs` pins
 * the DOM contract — after a tap, `document.activeElement` is still the field. It CANNOT prove
 * the virtual keyboard stayed up; a headless browser has none. That half needs one look on a
 * real handset.
 */

/** Width of ONE full-height trailing button (clear, reveal). */
export const CLEAR_GUTTER = 28
/**
 * Width of the STEPPER column. Narrower than a button on purpose: a stacked chevron pair needs
 * less width than an X, and a number field is often the narrowest thing on a form — a second
 * 28px reservation there is padding you can see.
 */
export const STEPPER_GUTTER = 16

/**
 * Input types a trailing rail belongs on. A `date` / `time` / `color` field already paints its
 * own control at the trailing edge and an X would sit on top of it; the non-text types
 * (checkbox, radio, file, submit…) have nothing to clear. `undefined` counts as text — that is
 * the element's own default.
 */
const TEXT_LIKE = new Set(['text', 'search', 'url', 'tel', 'email', 'password', 'number'])
export function isTextLike(type?: string): boolean {
  return type === undefined || type === '' || TEXT_LIKE.has(type)
}

/**
 * Write a field's value so BOTH controlled and uncontrolled callers notice.
 *
 * ⛔ Assigning `el.value = x` is INVISIBLE to React: it patches the value property on the element
 * instance, so React's change tracker sees no transition and never fires `onChange` — the field
 * looks changed while the state behind it still holds the old string, and the next render snaps
 * it back. Going through the PROTOTYPE setter and dispatching a real bubbling `input` event is
 * what makes React (which listens for `input`) treat it as a genuine user edit. Uncontrolled
 * callers reading the DOM see it too, so one path serves both.
 *
 * ⛔ This is also why the stepper below does NOT use `el.stepUp()` / `el.stepDown()` — they write
 * the value the invisible way, the exact trap this function exists to avoid.
 */
export function setFieldValue(el: HTMLInputElement | HTMLTextAreaElement, next: string): void {
  const proto =
    typeof HTMLTextAreaElement !== 'undefined' && el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  if (setter) setter.call(el, next)
  else el.value = next
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

/** Empty a field so BOTH controlled and uncontrolled callers notice. */
export function clearFieldValue(el: HTMLInputElement | HTMLTextAreaElement): void {
  setFieldValue(el, '')
}

/** How long the tick replaces the copy icon. Long enough to notice, short enough not to linger. */
export const COPIED_MS = 1600

/**
 * Put a field's value on the clipboard, and say whether it actually got there.
 *
 * ⛔ IT RETURNS A BOOLEAN BECAUSE IT CAN FAIL, AND A TICK OVER A FAILED COPY IS A LIE. The async
 * Clipboard API needs a secure context and a permission that can be refused, so the confirmation
 * is gated on the write, never on the click.
 *
 * ⚠ THE FALLBACK IS NOT `execCommand` ALONE — it SELECTS the text first and leaves it selected.
 * If both routes fail, the value is sitting highlighted under the cursor and Ctrl+C finishes the
 * job: the person still gets what they came for, which is the point of the button.
 */
export async function copyFieldValue(el: HTMLInputElement | HTMLTextAreaElement | null): Promise<boolean> {
  if (!el || el.value === '') return false

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(el.value)
      return true
    }
  } catch {
    // Refused or unavailable — fall through to the selection route rather than give up.
  }

  try {
    el.select()
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the only route left when the
    // Clipboard API is refused, and it is still what every browser implements.
    return document.execCommand('copy')
  } catch {
    return false
  }
}

/**
 * THE RAIL'S INK — measured from the field, not declared as a constant.
 *
 * ⛔⛔ WHY IT CANNOT BE A `color-mix` OFF `currentColor`. That was the first version, and on the
 * three staff portals it produced a BLACK icon that stayed black: the inherited text colour there
 * is already `rgb(10,10,10)`, so "50% toward black" is `rgb(5,5,5)` at rest and `rgb(2,2,2)` on
 * hover — measured luma 5 → 2 out of 255. Two states nobody can tell apart, and the only visible
 * change left was the OPACITY, which on a white field reads as the icon getting LIGHTER. That is
 * exactly what David reported twice (*"mouse over on a light input box still lightens the
 * icons"*), and no amount of adjusting the direction of a mix off `currentColor` could fix it.
 *
 * ★ So both states are computed from two things read off the live element:
 *   REST  = the field's own text colour, blended 45% toward its BACKGROUND — muted, whatever the
 *           theme. This is also David's *"50% darker"* on the viewer's dark fields.
 *   HOVER = the field's text colour at full strength.
 * ⇒ The direction takes care of itself: on a white field that is grey → near-black (darker, as
 *   asked); on the viewer's near-black field it is dim cream → full cream (lighter, because
 *   darker there would lose the icon in exactly the way lightening loses it on white).
 *
 * ⚠ Opacity no longer changes on hover. It was a second, opposing signal on top of the colour —
 *   the confound that made "which way did it go?" unanswerable by eye.
 */
const INK_FALLBACK = 'color-mix(in srgb, currentColor 55%, transparent)'

type Rgb = { r: number; g: number; b: number }

const parseRgb = (css: string): Rgb | null => {
  const n = css.match(/[\d.]+/g)?.map(Number)
  if (!n || n.length < 3) return null
  // Chrome answers `color-mix()` in `color(srgb 0..1)` and everything else in `rgb(0..255)`.
  const k = css.startsWith('color(') ? 255 : 1
  return { r: n[0] * k, g: n[1] * k, b: n[2] * k }
}

const css = (c: Rgb) => `rgb(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)})`
const blend = (a: Rgb, b: Rgb, t: number): Rgb => ({
  r: a.r + (b.r - a.r) * t,
  g: a.g + (b.g - a.g) * t,
  b: a.b + (b.b - a.b) * t,
})

/**
 * The colour actually painted behind this field.
 *
 * ⛔⛔ IT COMPOSITES ALPHA, AND THAT IS THE WHOLE DIFFICULTY. Several field styles in the estate
 * are a WHITE WASH OVER A DARK PAGE — `.tvl-field` is `rgb(255 255 255 / 6%)`, and so is the
 * harness. Taking the first painted background and ignoring its alpha calls those fields WHITE
 * and inverts everything downstream. Measured, not reasoned about: the first version did exactly
 * that, and the browser check caught it going 121 → 60 where it had to go up.
 */
function backdropOf(el: HTMLElement): Rgb {
  let out: Rgb = { r: 0, g: 0, b: 0 }
  let acc = 0
  for (let n: HTMLElement | null = el; n && acc < 0.999; n = n.parentElement) {
    const parts = getComputedStyle(n).backgroundColor.match(/[\d.]+/g)?.map(Number)
    if (!parts || parts.length < 3) continue
    const a = parts.length > 3 ? parts[3] : 1
    if (a === 0) continue
    const w = a * (1 - acc)
    out = { r: out.r + parts[0] * w, g: out.g + parts[1] * w, b: out.b + parts[2] * w }
    acc += w
  }
  // Nothing opaque anywhere up the tree: the canvas the browser paints is white.
  const w = 1 - acc
  return { r: out.r + 255 * w, g: out.g + 255 * w, b: out.b + 255 * w }
}

/** How far the resting ink sits from the field's text colour, towards its background. */
const REST_MUTE = 0.45

function inkFor(el: HTMLElement | null): { rest: string; hover: string } {
  if (!el || typeof getComputedStyle !== 'function') return { rest: INK_FALLBACK, hover: 'currentColor' }
  const fg = parseRgb(getComputedStyle(el).color)
  if (!fg) return { rest: INK_FALLBACK, hover: 'currentColor' }
  return { rest: css(blend(fg, backdropOf(el), REST_MUTE)), hover: css(fg) }
}

const buttonBase: CSSProperties = {
  display: 'grid',
  placeItems: 'center',
  padding: 0,
  margin: 0,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  // ⚠ `width` is in the transition because the CLEAR collapses rather than unmounting — that is
  //   what makes its neighbours FLOAT rather than jump. See `TrailingButton`.
  transition: 'opacity 120ms ease, color 120ms ease, width 120ms ease',
  overflow: 'hidden',
}

/** One trailing button. Full height, out of the tab order, focus-preserving. */
export function TrailingButton({
  shown,
  onPress,
  label,
  children,
  slot,
  width = CLEAR_GUTTER,
  ink,
}: {
  shown: boolean
  onPress: () => void
  label: string
  children: ReactNode
  /** DOM hook that survives `aria-hidden` — the hidden state is the one a role query cannot see. */
  slot: string
  width?: number
  /** Rest + hover colours, measured off the field. See `inkFor`. */
  ink: { rest: string; hover: string }
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button" // ⛔ NOT "submit": inside a <form> the default would post it.
      data-slot={slot}
      // ⛔ ALWAYS MOUNTED, hidden by opacity + pointer-events. Unmounting is the classic
      //   disappearing-button bug: pointer goes down, the button unmounts, the click lands on
      //   nothing. A control that can vanish from under a finger is the wrong shape to rely on.
      style={{
        ...buttonBase,
        // ⛔ COLLAPSES TO ZERO WIDTH, IT DOES NOT UNMOUNT. David, 2026-08-04: *"the password
        //   reveal should not be in a fixed position, it needs to float left when the X clear is
        //   displayed and then float right when focus is lost"*. The rail is right-aligned, so a
        //   hidden CLEAR taking no width lets the eye (and the stepper) slide to the edge, and
        //   they slide back when it returns. Unmounting would do the same thing visually and
        //   bring back the disappearing-button bug: pointer down, unmount, the click lands on
        //   nothing. ⚠ The field's PADDING does not follow — the gutter stays reserved, so the
        //   text never reflows underneath.
        width: shown ? width : 0,
        height: '100%',
        // ⚠ Opacity is the SHOW/HIDE channel only — never a hover signal. See the ink docblock.
        opacity: shown ? 1 : 0,
        pointerEvents: shown ? 'auto' : 'none',
        color: hover ? ink.hover : ink.rest,
      }}
      // Out of the tab order, matching a native `<input type="search">` clear button: a keyboard
      // user clears with Ctrl/Cmd+A then Delete, and this removes a whole class of focus bugs
      // rather than leaving them to be managed.
      tabIndex={-1}
      aria-hidden={!shown}
      aria-label={label}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseDown={(e) => e.preventDefault()} // ← the whole point. See the file docblock.
      onClick={onPress}
    >
      {children}
    </button>
  )
}

/**
 * One step of a number field, honouring the element's own `step` / `min` / `max` — the same
 * contract the native buttons had, so nothing an author already set changes meaning.
 *
 * An EMPTY field steps from ZERO and is then clamped, which is what lands it on `min` when there
 * is one — that is the native rule, and it matters: stepping from `min` DIRECTLY would skip it
 * (min=5 would give 6 on the first press, never 5). The clamp does that work; the base must not
 * try to do it as well.
 *
 * `toFixed` on the step's own decimal places kills float drift: `0.1 + 0.2` must read `0.3`.
 * `step="any"` has no meaningful increment, so it falls back to 1.
 */
function steppedValue(el: HTMLInputElement, dir: 1 | -1): string {
  const rawStep = Number(el.step)
  const step = Number.isFinite(rawStep) && rawStep > 0 ? rawStep : 1
  const min = el.min === '' ? -Infinity : Number(el.min)
  const max = el.max === '' ? Infinity : Number(el.max)
  const base = el.value === '' ? 0 : Number(el.value)
  if (!Number.isFinite(base)) return el.value
  let next = base + dir * step
  const dp = (String(step).split('.')[1] ?? '').length
  if (dp) next = Number(next.toFixed(dp))
  return String(Math.min(max, Math.max(min, next)))
}

/** How long a press must be held before it starts repeating, and how fast it repeats after that. */
const REPEAT_DELAY_MS = 400
const REPEAT_INTERVAL_MS = 60

/**
 * One chevron. Press-and-hold repeats, and because the repeat rides POINTER events it works
 * identically under a finger.
 *
 * ⛔⛔ THE FOCUS GUARD IS UNTOUCHED AND MUST STAY THAT WAY — `onMouseDown` → `preventDefault()`
 * is still the whole mechanism (on touch it is the COMPATIBILITY mousedown that moves focus, so
 * this one guard covers both). The repeat is wired to `pointerdown` and deliberately does NOT
 * cancel it.
 *
 * ⛔ The first step fires on POINTERDOWN, not on click, so a hold responds immediately rather
 * than after `REPEAT_DELAY_MS`. The trailing `click` is then swallowed, or every press would step
 * twice. `click` still steps on its own when no pointer sequence preceded it — which is what a
 * synthesized `.click()` from a test or an assistive tool does, and the live checks depend on it.
 *
 * ⚠ Touch needs three things a mouse does not: `touchAction: manipulation` (else the engine holds
 * the tap back waiting for a double-tap-zoom), no text selection + a cancelled `contextmenu`
 * (else a long press — which is now THE gesture — raises the selection callout over the field),
 * and POINTER CAPTURE, so a finger sliding off a 16px-wide button releases the timer instead of
 * stranding it.
 */
function StepButton({
  dir,
  shown,
  lit,
  onStep,
  children,
  label,
  ink,
}: {
  dir: 1 | -1
  shown: boolean
  ink: { rest: string; hover: string }
  /** Painted as pressed while the KEYBOARD is stepping — see `useTrailingAffordances`. */
  lit: boolean
  /** Performs one step; returns false when the value did not move (clamped at min/max). */
  onStep: (dir: 1 | -1) => boolean
  children: ReactNode
  label: string
}) {
  const [hover, setHover] = useState(false)
  const delay = useRef<ReturnType<typeof setTimeout> | null>(null)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)
  const steppedByPointer = useRef(false)

  const stop = useCallback(() => {
    if (delay.current) clearTimeout(delay.current)
    if (timer.current) clearInterval(timer.current)
    delay.current = null
    timer.current = null
  }, [])

  // A press held while the component unmounts would otherwise repeat forever against a dead ref.
  useEffect(() => stop, [stop])

  const start = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return // right/middle click is not a step
    steppedByPointer.current = true
    if (!onStep(dir)) return // already clamped — nothing to repeat towards
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Capture is a convenience, not a requirement; a browser refusing it just means the
      // pointerup may land elsewhere, and the handlers below still stop the timer.
    }
    delay.current = setTimeout(() => {
      timer.current = setInterval(() => {
        // Stop at the boundary rather than re-dispatching an identical `input` event 16x/second,
        // which would mark the form dirty over and over for no change.
        if (!onStep(dir)) stop()
      }, REPEAT_INTERVAL_MS)
    }, REPEAT_DELAY_MS)
  }

  return (
    <button
      type="button"
      data-slot={dir === 1 ? 'step-up' : 'step-down'}
      style={{
        ...buttonBase,
        flex: '1 1 0',
        width: '100%',
        minHeight: 0,
        touchAction: 'manipulation',
        userSelect: 'none',
        opacity: shown ? 1 : 0,
        pointerEvents: shown ? 'auto' : 'none',
        color: hover || lit ? ink.hover : ink.rest,
      }}
      // ↑/↓ already step from the keyboard (browser default), so these are a pointer affordance
      // only and belong out of the tab order, exactly like the clear.
      tabIndex={-1}
      aria-hidden={!shown}
      aria-label={label}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseDown={(e) => e.preventDefault()} // ← the focus guard. See the file docblock.
      onPointerDown={start}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        // The pointer path already stepped; swallow its trailing click exactly once.
        if (steppedByPointer.current) {
          steppedByPointer.current = false
          return
        }
        onStep(dir)
      }}
    >
      {children}
    </button>
  )
}

/**
 * The number field's stepper — two half-height buttons in one column, sharing the rail with the
 * clear, which stays right-most. `flex: 1 1 0` rather than a percentage height so each half
 * resolves against the stretched rail without depending on the parent having a definite height.
 */
function StepperColumn({
  shown,
  onStep,
  lit,
  ink,
}: {
  shown: boolean
  onStep: (dir: 1 | -1) => boolean
  lit: 1 | -1 | null
  ink: { rest: string; hover: string }
}) {
  return (
    <div
      data-slot="stepper"
      style={{ width: STEPPER_GUTTER, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <StepButton dir={1} shown={shown} lit={lit === 1} onStep={onStep} ink={ink} label="Increase">
        <ChevronIcon dir="up" />
      </StepButton>
      <StepButton dir={-1} shown={shown} lit={lit === -1} onStep={onStep} ink={ink} label="Decrease">
        <ChevronIcon dir="down" />
      </StepButton>
    </div>
  )
}

export interface TrailingAffordances {
  onFocus: FocusEventHandler<never>
  onBlur: FocusEventHandler<never>
  /**
   * ⛔ NEVER calls `preventDefault`. It only lights the matching chevron while ↑/↓ steps a number
   * field — the stepping itself stays the BROWSER DEFAULT.
   */
  onKeyDown: KeyboardEventHandler<never>
  onKeyUp: KeyboardEventHandler<never>
  /** The buttons, already wrapped in their full-height trailing rail. Render as the last child. */
  rail: ReactNode
  /** Total width to reserve as the field's trailing padding. */
  gutter: number
  /** What the input's `type` must actually be right now (reveal flips password → text). */
  effectiveType?: string
}

/**
 * Focus tracking + the trailing rail, for a field that wants the affordances. Returns handlers to
 * put on the field — composed with the caller's own, which must still run.
 */
export function useTrailingAffordances<T extends HTMLInputElement | HTMLTextAreaElement>(
  ref: { current: T | null },
  opts: {
    enabled: boolean
    /** `password` ⇒ also offer REVEAL; `number` ⇒ also offer the STEPPER. */
    type?: string
    onFocus?: FocusEventHandler<never>
    onBlur?: FocusEventHandler<never>
    onKeyDown?: KeyboardEventHandler<never>
    onKeyUp?: KeyboardEventHandler<never>
    /**
     * Fixed rail height instead of stretching to the field. ⚠ For a TEXTAREA: the box grows
     * downwards, and a button parked at the vertical middle of a tall box reads as belonging to
     * nothing. Pin it to the first row instead.
     */
    railHeight?: number
    /**
     * ★ COPY — opt-in, and the ONE affordance that works on a READ-ONLY field (David, 2026-08-05:
     * *"add a Copy to clipboard icon button at the right edge of the url display"*). A field
     * showing something you are meant to take elsewhere — a one-time sign-in link, a key — is
     * exactly the field with nothing to clear and nothing to reveal, so `enabled` is false and the
     * rail would otherwise never render at all. This is the one thing that can bring it back.
     *
     * ⛔ DEFAULT OFF. Every one of the ~70 fields in the estate goes through this hook; an
     * affordance that appeared on all of them because it was convenient here is a change nobody
     * asked for on 69 screens.
     */
    copyable?: boolean
  },
): TrailingAffordances {
  const [focused, setFocused] = useState(false)
  // Measured, not configured: the same component serves a near-black viewer and three white
  // portals, and neither can be told apart from a prop the call sites do not pass.
  const [ink, setInk] = useState<{ rest: string; hover: string }>({ rest: INK_FALLBACK, hover: 'currentColor' })
  const [revealed, setRevealed] = useState(false)
  // Which chevron the KEYBOARD is currently holding down, so the pointer affordance and the key
  // that does the same job visibly agree. Without this the stepper looks inert to anyone using
  // ↑/↓ and reads as a second, separate mechanism — which is exactly what it is not.
  const [keyLit, setKeyLit] = useState<1 | -1 | null>(null)
  // "Copied" is a moment, not a state: it reverts on a timer, and the timer is cleared on unmount
  // so a field closed inside the window cannot set state on a component that is gone.
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (copiedTimer.current !== null) clearTimeout(copiedTimer.current)
  }, [])
  useEffect(() => {
    setInk(inkFor(ref.current))
  }, [ref, opts.enabled])
  const isPassword = opts.type === 'password'
  const isNumber = opts.type === 'number'

  const onFocus: FocusEventHandler<never> = (e) => {
    setFocused(true)
    opts.onFocus?.(e)
  }
  const onBlur: FocusEventHandler<never> = (e) => {
    setFocused(false)
    // Re-mask on the way out. A password left readable after you have moved on is a shoulder-surf
    // waiting to happen, and reveal is only ever useful while you are typing into the field.
    setRevealed(false)
    // A key held as focus leaves never gets its keyup here, so the chevron would stay lit.
    setKeyLit(null)
    opts.onBlur?.(e)
  }
  const onKeyDown: KeyboardEventHandler<never> = (e) => {
    // ⛔ Read only — see the interface docblock. No `preventDefault`, ever.
    if (isNumber && !e.defaultPrevented) {
      if (e.key === 'ArrowUp') setKeyLit(1)
      else if (e.key === 'ArrowDown') setKeyLit(-1)
    }
    opts.onKeyDown?.(e)
  }
  const onKeyUp: KeyboardEventHandler<never> = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') setKeyLit(null)
    opts.onKeyUp?.(e)
  }

  // ★ THE CLEAR IS FOCUS-GATED, ITS NEIGHBOURS ARE NOT — David, 2026-08-04: *"the clear input X
  //   only needs to be displayed when the input has focus"*. An X is only ever wanted while you
  //   are editing, and one on every field at rest is visual noise on a form of eight. The eye and
  //   the stepper stay put: reveal answers *"what did I type?"* on a field you have not clicked
  //   yet, and a stepper you must focus before you can press is a stepper with an extra step.
  //   ⚠ The GUTTER is reserved either way (below), so nothing reflows when the X appears.
  const shownAlways = opts.enabled
  const shownOnFocus = opts.enabled && focused
  // ⛔ COPY IS NOT FOCUS-GATED, and it is counted even when `enabled` is false — that is the whole
  //   point of it: the fields that most want a copy button are read-only, where nothing else in
  //   this rail applies. It also sits BEFORE the clear, like the eye: CLEAR STAYS RIGHT-MOST.
  const copyable = opts.copyable === true
  const buttons = (opts.enabled ? (isPassword ? 2 : 1) : 0) + (copyable ? 1 : 0)
  const gutter = buttons * CLEAR_GUTTER + (opts.enabled && isNumber ? STEPPER_GUTTER : 0)

  const rail = buttons > 0 && (
    <span
      data-slot="rail"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        ...(opts.railHeight === undefined ? { bottom: 0 } : { height: opts.railHeight }),
        display: 'flex',
        alignItems: 'stretch',
        // ⚠ RIGHT-ALIGNED, and the width is the FULL reserved gutter: that combination is what
        //   lets the buttons float within a lane whose size never changes.
        justifyContent: 'flex-end',
        width: gutter,
      }}
    >
      {isPassword && (
        <TrailingButton
          shown={shownAlways}
          onPress={() => setRevealed((r) => !r)}
          label={revealed ? 'Hide password' : 'Show password'}
          slot="reveal"
          ink={ink}
        >
          <EyeIcon off={revealed} />
        </TrailingButton>
      )}
      {isNumber && (
        <StepperColumn
          shown={shownAlways}
          lit={keyLit}
          ink={ink}
          onStep={(dir) => {
            const el = ref.current
            if (!(el instanceof HTMLInputElement)) return false
            const next = steppedValue(el, dir)
            if (next === el.value) return false
            setFieldValue(el, next)
            return true
          }}
        />
      )}
      {copyable && (
        <TrailingButton
          shown
          onPress={() => {
            void copyFieldValue(ref.current).then((ok) => {
              if (!ok) return
              setCopied(true)
              if (copiedTimer.current !== null) clearTimeout(copiedTimer.current)
              copiedTimer.current = setTimeout(() => setCopied(false), COPIED_MS)
            })
          }}
          // ⚠ The LABEL changes, not just the icon. A tick alone is invisible to a screen reader
          //   and to anyone who cannot tell two small glyphs apart.
          label={copied ? 'Copied' : 'Copy to clipboard'}
          slot="copy"
          ink={ink}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </TrailingButton>
      )}
      {/* ⚠ MOUNTED WHENEVER THE FIELD CAN BE CLEARED, shown only on focus — that is the
          never-unmounts rule, and it is about not reflowing as focus comes and goes. When the
          field cannot be cleared at all (read-only, showing a copy button and nothing else) there
          is no such moment, and a permanently zero-width button nobody can ever reach is DOM that
          claims an affordance the field does not have. */}
      {opts.enabled && (
        <TrailingButton
          shown={shownOnFocus}
          onPress={() => {
            const el = ref.current
            if (el) clearFieldValue(el)
          }}
          label="Clear"
          slot="clear"
          ink={ink}
        >
          <XIcon />
        </TrailingButton>
      )}
    </span>
  )

  return {
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    rail,
    gutter,
    // ⛔ Reveal flips the ELEMENT's type, which is the only way to show the characters. It is a
    //   real type change, so a caller passing `type="password"` still gets a masked field
    //   whenever reveal is off — including after blur, which re-masks.
    effectiveType: isPassword && revealed ? 'text' : opts.type,
  }
}

/* ------------------------------------------------------------------ icons --
 * Inline SVG, no icon dependency: Monza draws these with lucide-react, and the donor rule is
 * copy, never depend. `aria-hidden` throughout — every button already carries its own label.
 */

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
}

/** Eye (masked) / struck-through eye (revealed) — the state shown is what the button WILL DO. */
export function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg width="18" height="18" {...svgProps}>
      {off ? (
        <>
          <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <path d="M1 1l22 22" />
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  )
}

function XIcon() {
  return (
    <svg width="16" height="16" {...svgProps}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

/** Two offset sheets — the copy glyph everyone already recognises. */
function CopyIcon() {
  return (
    <svg width="15" height="15" {...svgProps}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

/** The confirmation. Shown only after the clipboard write actually resolved. */
function CheckIcon() {
  return (
    <svg width="15" height="15" {...svgProps}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function ChevronIcon({ dir }: { dir: 'up' | 'down' }) {
  return (
    <svg width="12" height="12" {...svgProps}>
      <path d={dir === 'up' ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
    </svg>
  )
}
